/**
 * --doctor command: a friendly, beginner-oriented health check.
 *
 * Broader than --validate: it also checks the Node.js version, whether an update
 * is available, and which MCP clients already have the server wired up — then
 * prints a single, plain-English "next step" so a non-technical user always
 * knows what to do.
 *
 * Exit code 0 only when the core essentials (config + auth + connectivity) pass.
 */

import { loadConfig, resolveEnvironment, getConfigPath } from "../config/loader.js";
import { createAuthProviderFromConfig } from "../auth/wam-broker.js";
import { FlowManagementApi } from "../api/flow-management.js";
import { PowerPlatformAdminApi } from "../api/power-platform-admin-api.js";
import { DataverseApi } from "../api/dataverse-api.js";
import { findDataverseInstanceUrl } from "../api/dataverse-discovery.js";
import { ConfigurationError } from "../utils/errors.js";
import { getInstalledVersion, checkForUpdate } from "../utils/update-checker.js";
import { nativeStorageLoadable, repairNativeStorage, manualRepairInstruction } from "../setup/native-repair.js";
import { detectClientConfigs, detectClaudeCode, detectCodex } from "../setup/client-config.js";
import { checkSecondaryResourceAuth } from "./secondary-resources.js";
import { diagnoseCanvasAuthoringRuntime } from "./canvas-authoring-runtime.js";
import { formatFeatureList, normalizeFeatures } from "../config/features.js";

const c = {
  reset: "\x1b[0m",
  bold: "\x1b[1m",
  dim: "\x1b[2m",
  green: "\x1b[32m",
  yellow: "\x1b[33m",
  red: "\x1b[31m",
  cyan: "\x1b[36m",
};
const ok = `${c.green}✓${c.reset}`;
const bad = `${c.red}✗${c.reset}`;
const warn = `${c.yellow}⚠${c.reset}`;

// Doctor prints to stderr so it never collides with stdio MCP transport
// conventions, matching --validate.
function line(msg = ""): void {
  console.error(msg);
}

const MIN_NODE_MAJOR = 20;

export async function runDoctor(): Promise<void> {
  line("");
  line(`${c.bold}Power Automate MCP — Doctor${c.reset}`);
  line(`${c.dim}Checking your setup...${c.reset}`);
  line("");

  let essentialsOk = true;
  const nextSteps: string[] = [];

  // ── Native secure storage (npm install-script policy) ────────────────────
  // npm 12 blocks dependency install scripts by default; keytar's blocked
  // script leaves secure storage without its native binary — which breaks
  // everything downstream of sign-in while LOOKING like a consent problem.
  if (!nativeStorageLoadable()) {
    line(`  ${warn} Secure token storage components missing (npm blocked keytar's install script)`);
    line(`  ${c.dim}   Repairing (npm rebuild inside this package, 10-30s)...${c.reset}`);
    const repaired = await repairNativeStorage();
    if (repaired === "repaired") {
      line(`  ${ok} Repaired — secure token storage available`);
    } else {
      essentialsOk = false;
      line(`  ${bad} Automatic repair failed`);
      nextSteps.push(manualRepairInstruction());
    }
    line("");
  }

  // ── Node.js version ───────────────────────────────────────────────────────
  const nodeMajor = parseInt(process.versions.node.split(".")[0] ?? "0", 10);
  if (nodeMajor >= MIN_NODE_MAJOR) {
    line(`  ${ok} Node.js ${process.versions.node}`);
  } else {
    line(`  ${bad} Node.js ${process.versions.node} ${c.dim}(need ${MIN_NODE_MAJOR}+)${c.reset}`);
    nextSteps.push(`Install Node.js ${MIN_NODE_MAJOR} or newer from https://nodejs.org`);
    essentialsOk = false;
  }

  // ── Installed version + update check (informational) ──────────────────────
  const installed = getInstalledVersion();
  const update = await checkForUpdate();
  if (update?.updateAvailable) {
    line(`  ${warn} Version ${installed} ${c.dim}(latest is ${update.latestVersion})${c.reset}`);
    nextSteps.push("Update: npm install -g powerautomate-mcp@latest");
  } else if (update) {
    line(`  ${ok} Version ${installed} ${c.dim}(latest)${c.reset}`);
  } else {
    line(`  ${ok} Version ${installed} ${c.dim}(couldn't check for updates)${c.reset}`);
  }

  // ── Config file ───────────────────────────────────────────────────────────
  let config;
  const configPath = getConfigPath();
  try {
    config = loadConfig();
    line(`  ${ok} Config file`);
    line(`     ${c.dim}${configPath}${c.reset}`);
    line(`     ${c.dim}Features: ${formatFeatureList(normalizeFeatures(config.features.enabled))}${c.reset}`);
  } catch (err) {
    const msg = err instanceof ConfigurationError ? err.message : String(err);
    line(`  ${bad} Config file ${c.dim}— ${msg.split("\n")[0]}${c.reset}`);
    nextSteps.push("Run setup: powerautomate-mcp --setup");
    essentialsOk = false;
  }

  // ── Auth (silent) ─────────────────────────────────────────────────────────
  let authProvider: Awaited<ReturnType<typeof createAuthProviderFromConfig>> | undefined;
  if (config) {
    try {
      authProvider = await createAuthProviderFromConfig(
        config.auth.clientId,
        config.auth.tenantId,
        undefined,
        true // silentOnly
      );
      await authProvider.getToken();
      const account = authProvider.getCurrentAccount();
      line(`  ${ok} Signed in${account?.username ? ` ${c.dim}as ${account.username}${c.reset}` : ""}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const consent = /AADSTS65001|consent|approval/i.test(msg);
      line(`  ${bad} Sign-in ${c.dim}— ${consent ? "admin consent not granted yet" : msg.split("\n")[0]}${c.reset}`);
      nextSteps.push(
        consent
          ? "Ask an admin to approve the consent URL, then re-run: powerautomate-mcp --setup"
          : "Re-authenticate: powerautomate-mcp --setup"
      );
      essentialsOk = false;
    }
  }

  // ── Connectivity ──────────────────────────────────────────────────────────
  if (config && authProvider) {
    try {
      const env = resolveEnvironment(config);
      const flowApi = new FlowManagementApi({ authProvider, defaultEnvironmentId: env.id });
      await flowApi.listFlows({ environment: env.id, top: 1 });
      line(`  ${ok} Power Platform reachable ${c.dim}(${env.displayName ?? env.name})${c.reset}`);

      // Flow connectivity does not prove that Dataverse-backed tools work: the
      // two APIs use different resource audiences and hosts. Resolve the same
      // authoritative org URL used by the MCP server, then make a lightweight,
      // read-only WhoAmI request so --doctor cannot report a false green.
      const enabled = normalizeFeatures(config.features.enabled);
      if (enabled.includes("dataverse")) {
        let dataverseUrl = env.dataverseUrl;

        if (!dataverseUrl) {
          try {
            const adminApi = new PowerPlatformAdminApi({
              authProvider,
              defaultEnvironmentId: env.id,
            });
            const detail = await adminApi.getEnvironment(env.id);
            dataverseUrl = detail.properties?.linkedEnvironmentMetadata?.instanceUrl;
          } catch {
            // Some tenants return 404 from the BAP detail endpoint even though
            // the Flow environment list contains the authoritative URL.
          }
        }

        if (!dataverseUrl) {
          const environments = await flowApi.listEnvironments();
          dataverseUrl = findDataverseInstanceUrl(environments, env.id);
        }

        // Preserve the server's final legacy fallback, but verify it live here.
        dataverseUrl ??= DataverseApi.getDataverseUrl(env.id, env.region);
        const dataverseApi = new DataverseApi({ authProvider, environmentUrl: dataverseUrl });
        await dataverseApi.executeUnboundFunction("WhoAmI");
        const dataverseHost = new URL(
          dataverseUrl.startsWith("https://") ? dataverseUrl : `https://${dataverseUrl}`,
        ).hostname;
        line(`  ${ok} Dataverse reachable ${c.dim}(${dataverseHost})${c.reset}`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      line(`  ${bad} Power Platform ${c.dim}— ${msg.split("\n")[0]}${c.reset}`);
      nextSteps.push("Check your network / environment, then re-run --doctor");
      essentialsOk = false;
    }
  }

  // ── Secondary API authorization ───────────────────────────────────────────
  if (authProvider) {
    line("");
    line(`  ${c.bold}Advanced APIs${c.reset}`);
    const results = await checkSecondaryResourceAuth(authProvider, config?.features.enabled);
    if (results.length === 0) {
      line(`  ${ok} ${c.dim}No secondary API permissions selected${c.reset}`);
    } else {
      for (const result of results) {
        if (result.ok) {
          line(`  ${ok} ${result.label}`);
        } else {
          line(`  ${result.critical ? bad : warn} ${result.label} ${c.dim}— not authorized${c.reset}`);
          nextSteps.push(result.nextStep);
          if (result.critical) essentialsOk = false;
        }
      }
    }
  }

  // ── Canvas Authoring preview runtime (optional) ──────────────────────────
  if (config && normalizeFeatures(config.features.enabled).includes("powerApps")) {
    const canvasRuntime = await diagnoseCanvasAuthoringRuntime();
    if (canvasRuntime.ok) {
      const latestSdk = canvasRuntime.installedDotnetSdkVersions.at(-1);
      line(`  ${ok} Canvas authoring runtime${latestSdk ? ` ${c.dim}(.NET SDK ${latestSdk})${c.reset}` : ""}`);
    } else {
      line(`  ${warn} Canvas authoring preview ${c.dim}— ${canvasRuntime.classification}${c.reset}`);
      nextSteps.push(
        "Canvas authoring (optional): install .NET SDK 10+ so dotnet and dnx are on PATH, then restart your AI app",
      );
    }
  }

  // ── Connected AI clients (informational) ──────────────────────────────────
  line("");
  line(`  ${c.bold}AI apps${c.reset}`);
  const clients = [...detectClientConfigs(), await detectClaudeCode(), await detectCodex()];
  const wired = clients.filter((d) => d.wired);
  if (wired.length === 0) {
    line(`  ${warn} ${c.dim}No AI app is wired up yet${c.reset}`);
    nextSteps.push("Connect your app: powerautomate-mcp --setup  (or --client <name>)");
  } else {
    for (const d of wired) line(`  ${ok} ${d.label} ${c.dim}connected${c.reset}`);
    const notWired = clients.filter((d) => d.present && !d.wired);
    for (const d of notWired) line(`  ${c.dim}·  ${d.label} installed but not connected${c.reset}`);
  }

  // ── Summary ───────────────────────────────────────────────────────────────
  line("");
  if (essentialsOk) {
    line(`${c.green}${c.bold}  Everything essential is working.${c.reset}`);
    if (nextSteps.length > 0) {
      line("");
      line(`  ${c.dim}Optional:${c.reset}`);
      for (const s of nextSteps) line(`  ${c.dim}·${c.reset} ${s}`);
    }
    line("");
    process.exit(0);
  } else {
    line(`${c.yellow}${c.bold}  A few things need attention.${c.reset}`);
    line("");
    line(`  ${c.bold}Do this next:${c.reset}`);
    for (const s of nextSteps) line(`  ${c.cyan}→${c.reset} ${s}`);
    line("");
    process.exit(1);
  }
}
